import { useState, useEffect, useMemo } from "react";
import axios from "axios";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;
const ENV_DEV = import.meta.env.VITE_ENV_DEV;

interface Data {
  limit: number;
  offset: number;
  withCount: boolean;
  count: number;
  data: Immobile[];
}
interface DateDetails {
  insertion: number;
  update: number;
  auction: number;
  exclusiveEnd: number;
}
interface PriceDetails {
  price: number;
  negotiable: boolean;
  reservedNegotiation: boolean;
}
interface IncludeDetails {
  withCadastralData: boolean;
  withPhotos: boolean;
  withLayout: boolean;
  withVideo: boolean;
  withPanoramicPhotos: boolean;
  withVirtualTour: boolean;
  auction: boolean;
  auctionExpired?: boolean;
}
interface VetrinaDetails {
  number: number;
  position: number;
}
interface StatusDetails {
  id: number;
  name: string;
}
interface CategoryDetails {
  id: number;
  name: string;
}
interface AddressDetails {
  provinciaCode: string;
  provinciaName: string;
  provinciaSigla: string;
  comuneCode: string;
  comuneName: string;
  frazioneCode: number;
  frazioneName: string;
  quartiereCode: number;
  quartiereName: string;
}
interface TipologyDetails {
  id: number;
  name: string;
  seoString: string;
}
interface ContractDetails {
  id: number;
  name: string;
}
interface AuctionDetails {
  auction: boolean;
  date: number;
  expired: boolean;
}
interface DistanceDetails {
  id: number;
  name: string;
  mt: number;
}
interface Immobile {
  id: number;
  agencyId: number;
  name: string;
  link: string;
  description: string;
  ref: string;
  sqmt: number;
  seoString: string;
  exclusive: boolean;
  rooms: string;
  bathrooms: number;
  premises: number;
  dateDetails: DateDetails;
  priceDetails: PriceDetails;
  includeDetails: IncludeDetails;
  vetrinaDetails: VetrinaDetails;
  statusDetails: StatusDetails;
  categoryDetails: CategoryDetails;
  addressDetails: AddressDetails;
  tipologyDetails: TipologyDetails;
  contractDetails: ContractDetails;
  auctionDetails: AuctionDetails;
  distances: DistanceDetails[];
  mainImage: string;
}

interface GetPropertiesSoldParams {
  limit: number;
  offset: number;
  withCount: boolean;
  filters: {
    contract: number[] | null;
    type: number[];
    provincia: string;
    comune: string[];
    frazione: string[] | null;
    maxRooms: number;
    maxBathrooms: number;
    minPrice: number;
    maxPrice: number;
    sqmtMin: number;
    sqmtMax: number;
    gardenPrivate: boolean;
    terrace: boolean;
    swimmingPool: boolean;
    ref: string;
  };
  orderBy?: {
    field: string;
    direction: 'asc' | 'desc';
  }
  portal?: number;
}

export const useGetPropertiesSold = (agencyId: number, params: GetPropertiesSoldParams = {
  limit: 10000,
  offset: 0,
  withCount: false,
  filters: {
    contract: [],
    type: [],
    provincia: '',
    comune: [],
    frazione: [],
    maxRooms: 0,
    maxBathrooms: 0,
    minPrice: 0,
    maxPrice: 0,
    sqmtMin: 0,
    sqmtMax: 0,
    gardenPrivate: false,
    terrace: false,
    swimmingPool: false,
    ref: '',
  },
  orderBy: {
    field: '',
    direction: 'desc',
  },
  portal: 56
},
  skip: boolean = false
) => {
  const [properties, setProperties] = useState<Immobile[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const stringifiedParams = useMemo(() => JSON.stringify(params), [params]);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    const fetchProperties = async () => {
      const parsedParams: GetPropertiesSoldParams = JSON.parse(stringifiedParams);

      try {
        const { limit, offset, filters, orderBy } = parsedParams;
        const response = await axios.post(`${API_BASE_URL}/properties-sold`, {
          agencyId,
          limit: limit ?? 6,
          offset: offset ?? 0,
          withCount: true,
          filters,
          orderBy,
          portal: parsedParams.portal ?? 56,
        });

        if (ENV_DEV === true) {
          console.log("Response from /properties-sold:", response.data);
        }

        const data: Data = response.data;

        setProperties(data.data);
        setTotalCount(data.count);

        const formattedProperties: Immobile[] = await Promise.all(
          data.data.map(async (property: Immobile) => {
            const formattedProperty: Immobile = {
              id: property.id,
              agencyId: property.agencyId,
              name: `Immobile ${property.id}`,
              link: `#`,
              description: "Descrizione non disponibile.",
              ref: property.ref,
              sqmt: property.sqmt,
              seoString: property.seoString,
              exclusive: property.exclusive,
              rooms: property.rooms,
              bathrooms: property.bathrooms,
              premises: property.premises,
              dateDetails: property.dateDetails,
              priceDetails: property.priceDetails || {
                price: 0,
                negotiable: false,
                reservedNegotiation: false,
              },
              includeDetails: property.includeDetails,
              vetrinaDetails: property.vetrinaDetails,
              statusDetails: property.statusDetails,
              categoryDetails: property.categoryDetails,
              addressDetails: property.addressDetails || {
                provinciaCode: "",
                provinciaName: "",
                provinciaSigla: "",
                comuneCode: "",
                comuneName: "",
                frazioneCode: 0,
                frazioneName: "",
                quartiereCode: 0,
                quartiereName: "",
              },
              tipologyDetails: property.tipologyDetails,
              contractDetails: property.contractDetails,
              auctionDetails: property.auctionDetails,
              distances: property.distances,
              mainImage: await axios
                .head(property.mainImage)
                .then((response) => response.status === 200 ? property.mainImage : "/assets/images/no_image_big.png")
                .catch(() => "/assets/images/no_image_big.png"),
            };
            return formattedProperty;
          })
        );
        setProperties(formattedProperties);
      } catch (err) {
        console.error("Errore nel caricamento delle proprietà vendute:", err);
        setError("Errore nel caricamento delle proprietà vendute");
      } finally {
        setLoading(false);
      }
    };

    fetchProperties();
  }, [
    agencyId,
    skip,
    stringifiedParams
  ]);

  return { properties, totalCount, loading, error };
};